import React, { useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import style from './BottomNav.module.css';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuthUser } from '../../hooks/useAuthUser';
import { io } from 'socket.io-client';
import { useAuth } from '@clerk/clerk-react';

import { MdHome, MdExplore, MdChatBubble, MdSearch, MdPlayArrow } from 'react-icons/md';
import { UIContext } from '../../contexts/UIContext';
import { AppAuthContext } from '../../contexts/AppAuthContext';
import { buildApiBases, encodePathSegment, requestWithApiFallback } from '../../utils/apiRequest';
import { resolveSocketAuthPayload } from '../../utils/socketAuth';

const BottomNav = () => {
	useContext(UIContext);
	const { user } = useAuthUser();
	const { getToken } = useAuth();
	const { testAuthSession } = useContext(AppAuthContext);
	const location = useLocation();
	const API_URL = process.env.REACT_APP_API_URL;
	const [chatUnreadCount, setChatUnreadCount] = useState(0);
	const socketRef = useRef(null);
	const requestApi = useCallback((requestFactory) => requestWithApiFallback(API_URL, requestFactory), [API_URL]);
	const socketBaseUrl = useMemo(() => {
		const bases = buildApiBases(API_URL);
		return bases.find(base => !/\/api$/i.test(base)) || bases[0] || '';
	}, [API_URL]);

	useEffect(() => {
		if (!user?.id) return;
		const fetchUnread = async () => {
			try {
				const res = await requestApi((base) => axios.get(
					`${base}/chat/unread-count/${encodePathSegment(user.id)}`
				));
				setChatUnreadCount(res.data.unreadCount || 0);
			} catch { /* silently fail */ }
		};
		fetchUnread();
		const interval = setInterval(fetchUnread, 30000);
		return () => clearInterval(interval);
	}, [requestApi, user?.id]);

	useEffect(() => {
		if (!user?.id || !socketBaseUrl) return;
		let isCancelled = false;
		let socket = null;

		const refreshUnread = async () => {
			try {
				const res = await requestApi((base) => axios.get(
					`${base}/chat/unread-count/${encodePathSegment(user.id)}`
				));
				setChatUnreadCount(res.data.unreadCount || 0);
			} catch { /* silently fail */ }
		};

		const setupSocket = async () => {
			const authPayload = await resolveSocketAuthPayload({
				getToken,
				testAuthToken: testAuthSession?.token,
				userID: user.id
			});
			if (!authPayload || isCancelled) return;
			socket = io(socketBaseUrl, {
				transports: ['websocket', 'polling'],
				auth: authPayload
			});
			socketRef.current = socket;
			socket.on('chat:message', refreshUnread);
			socket.on('chat:messages-read', refreshUnread);
			socket.on('chat:conversation-created', refreshUnread);
		};

		setupSocket();

		return () => {
			isCancelled = true;
			if (socket) {
				socket.off('chat:message', refreshUnread);
				socket.off('chat:messages-read', refreshUnread);
				socket.off('chat:conversation-created', refreshUnread);
				socket.disconnect();
			}
			socketRef.current = null;
		};
	}, [getToken, requestApi, socketBaseUrl, testAuthSession?.token, user?.id]);

	const isActive = (path) => {
		if (path === '/' && location.pathname === '/') return true;
		if (path !== '/' && location.pathname.startsWith(path)) return true;
		return false;
	};

	const workoutsPath = user ? '/choice' : `/login?redirectTo=${encodeURIComponent('/choice')}`;
	const isWorkoutsActive = ['/choice', '/generated-stryds', '/stryd/', '/routine/']
		.some((routePrefix) => location.pathname.startsWith(routePrefix));

	const navItems = [
		{ path: '/', icon: MdHome, label: 'Home' },
		{ path: '/search', icon: MdSearch, label: 'Search' },
		{ path: workoutsPath, icon: MdPlayArrow, label: 'Workouts', variant: 'primary' },
		{ path: '/feed', icon: MdExplore, label: 'Feed' },
		{ path: '/chat', icon: MdChatBubble, label: 'Chat', badge: null },
	];

	return (
		<div className={style.BottomNav}>
			<div className={style.BottomNavWrapper}>
				{navItems.map((item) => {
					const active = item.variant === 'primary' ? isWorkoutsActive : isActive(item.path);
					if (item.variant === 'primary') {
						return (
							<Link to={item.path} key={item.label} className={style.PrimaryLink}>
								<button className={`${style.PrimaryButton} ${active ? style.PrimaryActive : ''}`} aria-label={item.label}>
									<span className={style.PrimaryIconWrap}>
										<item.icon className={style.PrimaryIcon} />
									</span>
									<span className={style.PrimaryText}>{item.label}</span>
								</button>
							</Link>
						);
					}

					return (
						<Link to={item.path} key={item.path}>
							<button className={`${style.IconButton} ${active ? style.Active : ''}`}>
								<div className={style.IconWrapper}>
									<item.icon className={style.Icon} />
									{item.path === '/chat' && chatUnreadCount > 0 && (
										<span className={style.Badge}>{chatUnreadCount > 9 ? '9+' : chatUnreadCount}</span>
									)}
								</div>
								<span className={style.ButtonText}>{item.label}</span>
							</button>
						</Link>
					);
				})}
			</div>
		</div>
	);
};

export default BottomNav;
